import React, { useEffect, useMemo, useState } from 'react';
import { BadgePercent, Edit2, Plus, RefreshCw, Tag, Trash2, Users } from 'lucide-react';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  setDoc,
  writeBatch
} from 'firebase/firestore/lite';
import apiClient from '../../api';
import { db, ensureFirebaseAuth } from '../../firebase';
import './AdminCoupons.css';

const COUPONS_COLLECTION = 'adminCoupons';
const FIREBASE_META_DOCUMENT_PREFIX = '_meta_';
const FIRESTORE_TIMEOUT_MS = 12000;
const CUSTOMER_PAGE_SIZE = 100;
const LEGACY_LOCAL_COUPONS_STORAGE_KEY = 'adminCouponsFallback';

const CATEGORY_OPTIONS = [
  'All Categories',
  'Groceries',
  'Electronics',
  'Fashion',
  'Beauty',
  'Home Essentials',
  'Beverages'
];

const USAGE_RULE_OPTIONS = [
  { value: 'all_orders', label: 'Every eligible order' },
  { value: 'first_order_only', label: 'First order only' },
  { value: 'one_use_per_user', label: 'One use per user' }
];

const seedCoupons = [
  {
    id: 'coupon-1',
    name: 'Ramzan Saver',
    description: '10% discount on grocery orders for recurring buyers.',
    category: 'Groceries',
    startDate: '2026-03-12',
    endDate: '2026-03-25',
    discountType: 'percentage',
    discountValue: '10',
    minimumOrderAmount: '1500',
    targetUser: 'all',
    targetUserLabel: 'All Users',
    usageRule: 'all_orders',
    firstOrderOnly: false,
    oneTimePerUser: false,
    perUserLimit: 0,
    createdAt: '2026-03-11T08:00:00.000Z'
  },
  {
    id: 'coupon-2',
    name: 'Welcome Flat 300',
    description: 'Flat discount for first-time customers in electronics.',
    category: 'Electronics',
    startDate: '2026-03-11',
    endDate: '2026-03-20',
    discountType: 'fixed',
    discountValue: '300',
    minimumOrderAmount: '2500',
    targetUser: 'all',
    targetUserLabel: 'All Users',
    usageRule: 'first_order_only',
    firstOrderOnly: true,
    oneTimePerUser: true,
    perUserLimit: 1,
    createdAt: '2026-03-11T08:05:00.000Z'
  }
];

const initialForm = {
  name: '',
  description: '',
  category: CATEGORY_OPTIONS[0],
  startDate: '',
  endDate: '',
  discountType: 'percentage',
  discountValue: '',
  minimumOrderAmount: '',
  targetUser: '',
  usageRule: 'all_orders'
};

const getCouponStatus = (coupon) => {
  const today = new Date();
  const start = new Date(coupon.startDate);
  const end = new Date(coupon.endDate);
  today.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  if (today < start) return 'Scheduled';
  if (today > end) return 'Expired';
  return 'Active';
};

const getDurationDays = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diff = end.getTime() - start.getTime();
  if (Number.isNaN(diff) || diff < 0) return 0;
  return Math.floor(diff / (1000 * 60 * 60 * 24)) + 1;
};

const formatDiscount = (coupon) => (
  coupon.discountType === 'percentage'
    ? `${coupon.discountValue}%`
    : `Rs. ${coupon.discountValue}`
);

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString();
};

const formatCurrencyAmount = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return '-';
  return `Rs. ${numericValue.toLocaleString()}`;
};

const normalizeUsageRule = (couponData = {}) => {
  const explicit = String(
    couponData.usageRule
      || couponData.usage_rule
      || couponData.restriction
      || couponData.restrictionType
      || couponData.usageType
      || ''
  ).trim().toLowerCase().replace(/[\s-]+/g, '_');

  if (['first_order_only', 'first_order', 'first_purchase', 'first_time'].includes(explicit)) {
    return 'first_order_only';
  }
  if (['one_use_per_user', 'once_per_user', 'single_use', 'one_time'].includes(explicit)) {
    return 'one_use_per_user';
  }
  if (couponData.firstOrderOnly === true || couponData.first_order_only === true) {
    return 'first_order_only';
  }
  if (
    couponData.oneTimePerUser === true
    || couponData.one_time_per_user === true
    || Number(couponData.perUserLimit || couponData.per_user_limit || 0) === 1
  ) {
    return 'one_use_per_user';
  }

  const searchableText = [
    couponData.id,
    couponData.name,
    couponData.code,
    couponData.couponCode,
    couponData.description
  ].filter(Boolean).join(' ').toLowerCase();

  return searchableText.includes('first') ? 'first_order_only' : 'all_orders';
};

const getUsageRuleLabel = (usageRule) => (
  USAGE_RULE_OPTIONS.find((option) => option.value === usageRule)?.label
  || USAGE_RULE_OPTIONS[0].label
);

const getCouponDocumentId = (name) => (
  String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[\\/]+/g, '-')
    .replace(/\s+/g, '-')
);

const getCustomerOptionLabel = (customer) => {
  const name = customer?.name?.trim();
  const email = customer?.email?.trim();
  const phone = customer?.phone?.trim();

  if (name && email) return `${name} (${email})`;
  if (name && phone) return `${name} (${phone})`;
  if (name) return name;
  if (email) return email;
  if (phone) return phone;
  return `User ${customer?.customer_id || ''}`.trim();
};

const normalizeCoupon = (couponId, couponData) => ({
  id: couponId,
  name: couponData.name || '',
  description: couponData.description || '',
  category: couponData.category || CATEGORY_OPTIONS[0],
  startDate: couponData.startDate || '',
  endDate: couponData.endDate || '',
  discountType: couponData.discountType || 'percentage',
  discountValue: String(couponData.discountValue || ''),
  minimumOrderAmount: String(couponData.minimumOrderAmount || ''),
  targetUser: couponData.targetUser || '',
  targetUserLabel: couponData.targetUserLabel || couponData.targetUser || 'Unassigned User',
  usageRule: normalizeUsageRule({ ...couponData, id: couponId }),
  createdAt: couponData.createdAt || new Date().toISOString()
});

const sortCoupons = (items) => (
  [...items].sort((left, right) => new Date(right.createdAt) - new Date(left.createdAt))
);

const formatFirebaseError = (error, fallbackMessage) => {
  const errorCode = error?.code ? ` (${error.code})` : '';
  const errorMessage = error?.message || fallbackMessage;
  return `${fallbackMessage}${errorCode}: ${errorMessage}`;
};

const withTimeout = (promise, timeoutMessage) => (
  Promise.race([
    promise,
    new Promise((_, reject) => {
      window.setTimeout(() => reject(new Error(timeoutMessage)), FIRESTORE_TIMEOUT_MS);
    })
  ])
);

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [formData, setFormData] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [formError, setFormError] = useState('');
  const [syncError, setSyncError] = useState('');
  const [customerLoadError, setCustomerLoadError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);

  const targetUserOptions = useMemo(() => {
    const options = [
      { value: '', label: 'Select User' },
      { value: 'all', label: 'All Users' },
      ...customerOptions
    ];

    if (
      formData.targetUser
      && !options.some((option) => option.value === formData.targetUser)
    ) {
      const matchingCoupon = coupons.find((coupon) => coupon.targetUser === formData.targetUser);
      options.push({
        value: formData.targetUser,
        label: matchingCoupon?.targetUserLabel || 'Selected User'
      });
    }

    return options;
  }, [coupons, customerOptions, formData.targetUser]);

  const loadCustomerOptions = async () => {
    setIsLoadingUsers(true);
    setCustomerLoadError('');

    try {
      let page = 1;
      let totalPages = 1;
      const allCustomers = [];

      while (page <= totalPages) {
        const response = await withTimeout(
          apiClient.get('/api/customerdetails', {
            params: {
              page,
              limit: CUSTOMER_PAGE_SIZE
            }
          }),
          'Timed out while loading app users.'
        );

        const responseCustomers = Array.isArray(response?.data?.customers)
          ? response.data.customers
          : [];

        allCustomers.push(...responseCustomers);

        const reportedTotalPages = Number(response?.data?.pagination?.total_pages || 1);
        totalPages = Number.isFinite(reportedTotalPages) && reportedTotalPages > 0 ? reportedTotalPages : 1;
        page += 1;
      }

      const dedupedCustomers = Array.from(
        new Map(
          allCustomers
            .filter((customer) => customer?.customer_id)
            .map((customer) => [
              String(customer.customer_id),
              {
                value: String(customer.customer_id),
                label: getCustomerOptionLabel(customer)
              }
            ])
        ).values()
      ).sort((left, right) => left.label.localeCompare(right.label));

      setCustomerOptions(dedupedCustomers);
    } catch (error) {
      console.error('Failed to load app users', error);
      setCustomerOptions([]);
      setCustomerLoadError('Could not load app users. You can still assign coupons to All Users.');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const loadCoupons = async () => {
    setIsLoading(true);
    setSyncError('');

    try {
      await ensureFirebaseAuth();

      const couponsCollection = collection(db, COUPONS_COLLECTION);
      const snapshot = await withTimeout(
        getDocs(couponsCollection),
        'Timed out while loading coupons from Firestore.'
      );

      if (snapshot.empty) {
        const batch = writeBatch(db);

        seedCoupons.forEach((coupon) => {
          const couponId = getCouponDocumentId(coupon.name);
          const couponRef = doc(couponsCollection, couponId);
          batch.set(couponRef, {
            ...coupon,
            id: couponId
          });
        });

        await withTimeout(
          batch.commit(),
          'Timed out while seeding default coupons in Firestore.'
        );
        setCoupons(sortCoupons(seedCoupons));
        return;
      }

      const loadedCoupons = snapshot.docs
        .filter((couponDoc) => !couponDoc.id.startsWith(FIREBASE_META_DOCUMENT_PREFIX))
        .map((couponDoc) => normalizeCoupon(couponDoc.id, couponDoc.data()));
      setCoupons(sortCoupons(loadedCoupons));
    } catch (error) {
      console.error('Failed to load coupons from Firebase', error);
      setCoupons([]);
      setSyncError(formatFirebaseError(
        error,
        'Could not load coupons from Firestore'
      ));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    window.localStorage.removeItem(LEGACY_LOCAL_COUPONS_STORAGE_KEY);
    loadCoupons();
    loadCustomerOptions();
  }, []);

  const filteredCoupons = useMemo(() => {
    return coupons.filter((coupon) => {
      const status = getCouponStatus(coupon);
      const normalizedQuery = searchQuery.trim().toLowerCase();
      const matchesQuery = !normalizedQuery
        || coupon.name.toLowerCase().includes(normalizedQuery)
        || coupon.description.toLowerCase().includes(normalizedQuery)
        || coupon.targetUserLabel.toLowerCase().includes(normalizedQuery);
      const matchesStatus = statusFilter === 'All' || status === statusFilter;
      const matchesCategory = categoryFilter === 'All' || coupon.category === categoryFilter;
      return matchesQuery && matchesStatus && matchesCategory;
    });
  }, [categoryFilter, coupons, searchQuery, statusFilter]);

  const stats = useMemo(() => {
    return coupons.reduce((accumulator, coupon) => {
      const status = getCouponStatus(coupon);
      accumulator.total += 1;
      accumulator[status.toLowerCase()] += 1;
      return accumulator;
    }, { total: 0, active: 0, scheduled: 0, expired: 0 });
  }, [coupons]);

  const resetForm = () => {
    setFormData(initialForm);
    setEditingId(null);
    setFormError('');
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');
    setSyncError('');

    if (!formData.name.trim() || !formData.description.trim()) {
      setFormError('Name and description are required.');
      return;
    }

    if (!formData.startDate || !formData.endDate) {
      setFormError('Start and end dates are required.');
      return;
    }

    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      setFormError('End date cannot be earlier than start date.');
      return;
    }

    if (Number(formData.discountValue) <= 0) {
      setFormError('Discount value must be greater than zero.');
      return;
    }

    if (Number(formData.minimumOrderAmount) <= 0) {
      setFormError('Minimum order amount must be greater than zero.');
      return;
    }

    if (!formData.targetUser) {
      setFormError('Please select a user or choose All Users.');
      return;
    }

    const couponDocumentId = getCouponDocumentId(formData.name);
    if (!couponDocumentId) {
      setFormError('Coupon name is not valid for a document ID.');
      return;
    }

    const duplicateCoupon = coupons.find((coupon) => (
      coupon.id === couponDocumentId && coupon.id !== editingId
    ));

    if (duplicateCoupon) {
      setFormError('A coupon with this name already exists.');
      return;
    }

    const selectedTargetUser = targetUserOptions.find((option) => option.value === formData.targetUser);
    const usageRule = normalizeUsageRule(formData);

    const payload = {
      id: couponDocumentId,
      name: formData.name.trim(),
      description: formData.description.trim(),
      category: formData.category,
      startDate: formData.startDate,
      endDate: formData.endDate,
      discountType: formData.discountType,
      discountValue: String(formData.discountValue),
      minimumOrderAmount: String(formData.minimumOrderAmount),
      targetUser: formData.targetUser,
      targetUserLabel: selectedTargetUser?.label || 'Selected User',
      usageRule,
      firstOrderOnly: usageRule === 'first_order_only',
      oneTimePerUser: usageRule === 'first_order_only' || usageRule === 'one_use_per_user',
      perUserLimit: usageRule === 'all_orders' ? 0 : 1,
      createdAt: editingId
        ? coupons.find((coupon) => coupon.id === editingId)?.createdAt || new Date().toISOString()
        : new Date().toISOString()
    };

    setIsSaving(true);

    try {
      await ensureFirebaseAuth();

      let savedCoupon;

      if (editingId) {
        if (editingId !== couponDocumentId) {
          const batch = writeBatch(db);
          batch.delete(doc(db, COUPONS_COLLECTION, editingId));
          batch.set(doc(db, COUPONS_COLLECTION, couponDocumentId), payload);
          await withTimeout(
            batch.commit(),
            'Timed out while renaming the coupon in Firestore.'
          );
        } else {
          await withTimeout(
            setDoc(doc(db, COUPONS_COLLECTION, editingId), payload),
            'Timed out while updating the coupon in Firestore.'
          );
        }
        savedCoupon = { ...payload };
      } else {
        await withTimeout(
          setDoc(doc(db, COUPONS_COLLECTION, couponDocumentId), payload),
          'Timed out while creating the coupon in Firestore.'
        );
        savedCoupon = { ...payload };
      }

      setCoupons((current) => {
        if (editingId) {
          return sortCoupons([
            ...current.filter((coupon) => coupon.id !== editingId),
            savedCoupon
          ]);
        }

        return sortCoupons([savedCoupon, ...current]);
      });

      resetForm();
    } catch (error) {
      console.error('Failed to save coupon to Firebase', error);
      setSyncError(formatFirebaseError(
        error,
        'Could not save the coupon to Firestore'
      ));
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (coupon) => {
    setEditingId(coupon.id);
    setFormData({
      name: coupon.name,
      description: coupon.description,
      category: coupon.category,
      startDate: coupon.startDate,
      endDate: coupon.endDate,
      discountType: coupon.discountType,
      discountValue: coupon.discountValue,
      minimumOrderAmount: coupon.minimumOrderAmount || '',
      targetUser: coupon.targetUser,
      usageRule: coupon.usageRule || 'all_orders'
    });
    setFormError('');
  };

  const handleDelete = async (couponId) => {
    const coupon = coupons.find((item) => item.id === couponId);
    if (!coupon) return;

    const shouldDelete = window.confirm(`Delete coupon "${coupon.name}"?`);
    if (!shouldDelete) return;

    setSyncError('');

    try {
      await ensureFirebaseAuth();

      await withTimeout(
        deleteDoc(doc(db, COUPONS_COLLECTION, couponId)),
        'Timed out while deleting the coupon from Firestore.'
      );
      setCoupons((current) => current.filter((item) => item.id !== couponId));
      if (editingId === couponId) {
        resetForm();
      }
    } catch (error) {
      console.error('Failed to delete coupon from Firebase', error);
      setSyncError(formatFirebaseError(
        error,
        'Could not delete the coupon from Firestore'
      ));
    }
  };

  const handleResetStorage = async () => {
    setSyncError('');
    setIsSaving(true);

    try {
      await ensureFirebaseAuth();

      const couponsCollection = collection(db, COUPONS_COLLECTION);
      const snapshot = await withTimeout(
        getDocs(couponsCollection),
        'Timed out while loading coupon data for reset.'
      );
      const batch = writeBatch(db);

      snapshot.forEach((couponDoc) => {
        batch.delete(couponDoc.ref);
      });

      seedCoupons.forEach((coupon) => {
        const couponId = getCouponDocumentId(coupon.name);
        const couponRef = doc(couponsCollection, couponId);
        batch.set(couponRef, {
          ...coupon,
          id: couponId
        });
      });

      await withTimeout(
        batch.commit(),
        'Timed out while resetting coupon data in Firestore.'
      );
      setCoupons(sortCoupons(seedCoupons));
      resetForm();
    } catch (error) {
      console.error('Failed to reset coupons in Firebase', error);
      setSyncError(formatFirebaseError(
        error,
        'Could not reset the default coupon data in Firestore'
      ));
    } finally {
      setIsSaving(false);
    }
  };

  const durationDays = getDurationDays(formData.startDate, formData.endDate);

  return (
    <div className="admin-coupons">
      <div className="admin-coupons-header">
        <div>
          <h1>Coupons Management</h1>
          <p>Coupons are synced through Firebase Firestore.</p>
        </div>
        <button
          type="button"
          className="coupon-btn coupon-btn-secondary"
          onClick={handleResetStorage}
          disabled={isLoading || isSaving}
        >
          <RefreshCw size={18} />
          Reset Demo Data
        </button>
      </div>

      {syncError ? <div className="coupon-sync-error">{syncError}</div> : null}
      {customerLoadError ? <div className="coupon-sync-error">{customerLoadError}</div> : null}

      <div className="coupon-stats-grid">
        <div className="coupon-stat-card">
          <div className="coupon-stat-icon blue">
            <BadgePercent size={20} />
          </div>
          <div>
            <span className="coupon-stat-label">Total Coupons</span>
            <strong>{stats.total}</strong>
          </div>
        </div>
        <div className="coupon-stat-card">
          <div className="coupon-stat-icon green">
            <Tag size={20} />
          </div>
          <div>
            <span className="coupon-stat-label">Active</span>
            <strong>{stats.active}</strong>
          </div>
        </div>
        <div className="coupon-stat-card">
          <div className="coupon-stat-icon amber">
            <RefreshCw size={20} />
          </div>
          <div>
            <span className="coupon-stat-label">Scheduled</span>
            <strong>{stats.scheduled}</strong>
          </div>
        </div>
        <div className="coupon-stat-card">
          <div className="coupon-stat-icon rose">
            <Users size={20} />
          </div>
          <div>
            <span className="coupon-stat-label">Expired</span>
            <strong>{stats.expired}</strong>
          </div>
        </div>
      </div>

      <div className="coupon-layout">
        <div className="coupon-form-card">
          <div className="coupon-section-head">
            <div>
              <h2>{editingId ? 'Edit Coupon' : 'Create Coupon'}</h2>
              <p>Manage the name, description, date range, category, discount, minimum order amount, and target user here.</p>
            </div>
          </div>

          <form className="coupon-form" onSubmit={handleSubmit}>
            <label className="coupon-field">
              <span>Coupon Name</span>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g. Eid Mega Sale"
                disabled={isLoading || isSaving}
              />
            </label>

            <label className="coupon-field">
              <span>Description</span>
              <textarea
                name="description"
                rows="4"
                value={formData.description}
                onChange={handleChange}
                placeholder="Short coupon detail for admin reference"
                disabled={isLoading || isSaving}
              />
            </label>

            <div className="coupon-grid two">
              <label className="coupon-field">
                <span>Start Date</span>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  disabled={isLoading || isSaving}
                />
              </label>
              <label className="coupon-field">
                <span>End Date</span>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  disabled={isLoading || isSaving}
                />
              </label>
            </div>

            <div className="coupon-grid two">
              <label className="coupon-field">
                <span>Category</span>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  disabled={isLoading || isSaving}
                >
                  {CATEGORY_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
              <label className="coupon-field">
                <span>Target User</span>
                <select
                  name="targetUser"
                  value={formData.targetUser}
                  onChange={handleChange}
                  disabled={isLoading || isSaving || isLoadingUsers}
                >
                  {targetUserOptions.map((option) => (
                    <option key={option.value || 'select-user'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {isLoadingUsers ? <small className="coupon-field-hint">Loading app users...</small> : null}
              </label>
            </div>

            <div className="coupon-grid two">
              <label className="coupon-field">
                <span>Discount Type</span>
                <select
                  name="discountType"
                  value={formData.discountType}
                  onChange={handleChange}
                  disabled={isLoading || isSaving}
                >
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed Amount</option>
                </select>
              </label>
              <label className="coupon-field">
                <span>Discount Value</span>
                <input
                  type="number"
                  min="1"
                  name="discountValue"
                  value={formData.discountValue}
                  onChange={handleChange}
                  placeholder={formData.discountType === 'percentage' ? 'e.g. 15' : 'e.g. 500'}
                  disabled={isLoading || isSaving}
                />
              </label>
            </div>

            <label className="coupon-field">
              <span>Minimum Order Amount</span>
              <input
                type="number"
                min="1"
                name="minimumOrderAmount"
                value={formData.minimumOrderAmount}
                onChange={handleChange}
                placeholder="e.g. 2000"
                disabled={isLoading || isSaving}
              />
            </label>

            <label className="coupon-field">
              <span>Usage Rule</span>
              <select
                name="usageRule"
                value={formData.usageRule}
                onChange={handleChange}
                disabled={isLoading || isSaving}
              >
                {USAGE_RULE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <small className="coupon-field-hint">
                First order and one-use coupons are checked per customer in the app.
              </small>
            </label>

            <div className="coupon-duration-box">
              <span>Coupon Duration</span>
              <strong>{durationDays ? `${durationDays} day(s)` : 'Select valid dates'}</strong>
            </div>

            {formError ? <div className="coupon-form-error">{formError}</div> : null}

            <div className="coupon-form-actions">
              <button
                type="button"
                className="coupon-btn coupon-btn-secondary"
                onClick={resetForm}
                disabled={isLoading || isSaving}
              >
                Clear
              </button>
              <button type="submit" className="coupon-btn coupon-btn-primary" disabled={isLoading || isSaving}>
                <Plus size={18} />
                {isSaving ? 'Saving...' : (editingId ? 'Update Coupon' : 'Save Coupon')}
              </button>
            </div>
          </form>
        </div>

        <div className="coupon-list-card">
          <div className="coupon-section-head">
            <div>
              <h2>Coupon List</h2>
              <p>The list is loaded from Firebase Firestore.</p>
            </div>
          </div>

          <div className="coupon-toolbar">
            <input
              type="text"
              className="coupon-search"
              placeholder="Search by name, description, or user..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              disabled={isLoading}
            />
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} disabled={isLoading}>
              <option value="All">All Status</option>
              <option value="Active">Active</option>
              <option value="Scheduled">Scheduled</option>
              <option value="Expired">Expired</option>
            </select>
            <select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)} disabled={isLoading}>
              <option value="All">All Categories</option>
              {CATEGORY_OPTIONS.filter((option) => option !== 'All Categories').map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {isLoading ? (
            <div className="coupon-empty-state">
              <RefreshCw size={44} className="coupon-spin" />
              <h3>Loading coupons</h3>
              <p>Fetching coupon data from Firestore.</p>
            </div>
          ) : filteredCoupons.length ? (
            <div className="coupon-table-wrap">
              <table className="coupon-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Category</th>
                    <th>Target User</th>
                    <th>Discount</th>
                    <th>Min Order</th>
                    <th>Usage</th>
                    <th>Duration</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCoupons.map((coupon) => {
                    const status = getCouponStatus(coupon);
                    return (
                      <tr key={coupon.id}>
                        <td>
                          <div className="coupon-name-cell">
                            <strong>{coupon.name}</strong>
                            <span>{coupon.description}</span>
                          </div>
                        </td>
                        <td>{coupon.category}</td>
                        <td>{coupon.targetUserLabel}</td>
                        <td>{formatDiscount(coupon)}</td>
                        <td>{formatCurrencyAmount(coupon.minimumOrderAmount)}</td>
                        <td>{getUsageRuleLabel(coupon.usageRule)}</td>
                        <td>
                          <div className="coupon-date-cell">
                            <span>{formatDate(coupon.startDate)}</span>
                            <span>{formatDate(coupon.endDate)}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`coupon-status ${status.toLowerCase()}`}>{status}</span>
                        </td>
                        <td>
                          <div className="coupon-actions">
                            <button
                              type="button"
                              className="coupon-icon-btn"
                              onClick={() => handleEdit(coupon)}
                              disabled={isSaving}
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              type="button"
                              className="coupon-icon-btn danger"
                              onClick={() => handleDelete(coupon.id)}
                              disabled={isSaving}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="coupon-empty-state">
              <BadgePercent size={44} />
              <h3>No coupons found</h3>
              <p>{syncError ? 'Reload the collection after fixing Firestore access.' : 'Try clearing the filters or create a new coupon.'}</p>
              {syncError ? (
                <button
                  type="button"
                  className="coupon-btn coupon-btn-secondary"
                  onClick={loadCoupons}
                  disabled={isLoading || isSaving}
                >
                  Retry Load
                </button>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCoupons;
